import { useEffect, useState } from 'react'
import { NavLink, Navigate, useLocation } from 'react-router-dom'
import { usePageAccess } from '@/hooks/usePageAccess'
import PurchasingProducts from './PurchasingProducts'
import PurchasingReview from './PurchasingReview'
import PurchasingSettings from './PurchasingSettings'
import PurchasingVendorItems from './PurchasingVendorItems'
import { purchasingTabs, tabFromPath, type PurchasingTabId } from './purchasingTabs'

export { tabFromPath } from './purchasingTabs'

export default function PurchasingLayout() {
  usePageAccess('purchasing')
  const location = useLocation()
  const activeTab = tabFromPath(location.pathname)
  const [mountedTabs, setMountedTabs] = useState<Set<PurchasingTabId>>(() =>
    activeTab ? new Set([activeTab]) : new Set(),
  )

  useEffect(() => {
    if (!activeTab) return
    setMountedTabs((prev) => {
      if (prev.has(activeTab)) return prev
      return new Set([...prev, activeTab])
    })
  }, [activeTab])

  if (location.pathname === '/purchasing' || location.pathname === '/purchasing/') {
    return <Navigate to="/purchasing/review" replace />
  }

  if (!activeTab) {
    return <Navigate to="/purchasing/review" replace />
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="bg-white border-b border-gray-200 shrink-0">
        <div className="px-4 lg:px-6 py-3">
          <h1 className="text-xl font-semibold text-gray-900">Purchasing</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Product matching, vendor items, and purchase ledger review
          </p>
        </div>

        <div className="border-t border-gray-200 px-4 lg:px-6 py-4">
          <div className="overflow-x-auto">
            <div
              className="inline-flex gap-2 bg-gray-200 p-1 rounded-lg min-w-max"
              role="tablist"
              aria-label="Purchasing sections"
            >
              {purchasingTabs.map((tab) => (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  role="tab"
                  className={({ isActive }) =>
                    `px-4 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                      isActive
                        ? 'bg-white text-brand-700 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`
                  }
                >
                  {tab.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-3 py-3 sm:px-6 sm:py-4 lg:px-8">
        <div className="min-h-0 flex-1 overflow-hidden">
          {mountedTabs.has('review') ? (
            <div className={activeTab === 'review' ? 'h-full min-h-0' : 'hidden'}>
              <PurchasingReview />
            </div>
          ) : null}
          {mountedTabs.has('products') ? (
            <div className={activeTab === 'products' ? 'h-full min-h-0' : 'hidden'}>
              <PurchasingProducts />
            </div>
          ) : null}
          {mountedTabs.has('vendor-items') ? (
            <div className={activeTab === 'vendor-items' ? 'h-full min-h-0' : 'hidden'}>
              <PurchasingVendorItems />
            </div>
          ) : null}
          {mountedTabs.has('settings') ? (
            <div className={activeTab === 'settings' ? 'h-full min-h-0' : 'hidden'}>
              <PurchasingSettings />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
